import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import React from "react";

export interface RawDataTableConfig {
    data: Record<string, any>[];
    fixField?: string | string[];
    priorityFields?: string[];
    excludeFields?: string[];
    fieldLabels?: Record<string, string>;
    customRenderers?: Record<string, (value: any, record: Record<string, any>) => React.ReactNode>;
    tableProps?: any;
}

export const generateRawDataTableColumns = (
    data: Record<string, any>[],
    config: Omit<RawDataTableConfig, "data"> = {},
): ColumnsType<Record<string, any>> => {
    const { fixField, priorityFields = [], excludeFields = [], fieldLabels = {}, customRenderers = {} } = config;

    const fixFields = Array.isArray(fixField) ? fixField : fixField ? [fixField] : [];

    if (!data || data.length === 0) {
        return [];
    }

    const allKeys = Array.from(new Set(data.flatMap((item) => Object.keys(item))));

    const filteredKeys = allKeys.filter((key) => {
        if (excludeFields.includes(key)) return false;
        return true;
    });

    const fixedPriorityFields = priorityFields.filter(
        (field) => filteredKeys.includes(field) && fixFields.includes(field),
    );
    const nonFixedPriorityFields = priorityFields.filter(
        (field) => filteredKeys.includes(field) && !fixFields.includes(field),
    );
    const fixedNonPriorityFields = fixFields.filter(
        (field) => filteredKeys.includes(field) && !priorityFields.includes(field),
    );
    const remainingFields = filteredKeys.filter((key) => !fixFields.includes(key) && !priorityFields.includes(key));

    const sortedKeys = [
        ...fixedPriorityFields,
        ...fixedNonPriorityFields,
        ...nonFixedPriorityFields,
        ...remainingFields,
    ];

    const columns: ColumnsType<Record<string, any>> = sortedKeys.map((key) => {
        const column: any = {
            title: fieldLabels[key] || formatFieldName(key),
            dataIndex: key,
            key: key,
            sorter: (a: any, b: any) => {
                const aVal = a[key];
                const bVal = b[key];

                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return -1;
                if (bVal == null) return 1;
                if (typeof aVal === "number" && typeof bVal === "number") {
                    return aVal - bVal;
                }

                return String(aVal).localeCompare(String(bVal));
            },
        };

        if (customRenderers[key]) {
            column.render = customRenderers[key];
        } else {
            column.render = (value: any) => {
                if (value == null) {
                    return <span style={{ color: "#999" }}>-</span>;
                }

                if (typeof value === "number") {
                    if (value % 1 !== 0) {
                        return value.toFixed(4);
                    }
                    return value.toLocaleString();
                }

                if (typeof value === "boolean") {
                    return value ? "Yes" : "No";
                }

                return String(value);
            };
        }

        if (fixFields.includes(key)) {
            column.fixed = "left";
            column.width = 150;
            column.render = (value: any) => {
                if (value == null) {
                    return <span style={{ color: "#999", fontStyle: "italic" }}>-</span>;
                }
                return <strong>{String(value)}</strong>;
            };

            const uniqueValues = Array.from(new Set(data.map((item) => item[key]).filter((val) => val != null)));
            if (uniqueValues.length > 0 && uniqueValues.length <= 100) {
                const sortedValues = uniqueValues.sort((a, b) => {
                    if (typeof a === "number" && typeof b === "number") {
                        return a - b;
                    }
                    return String(a).localeCompare(String(b));
                });

                column.filters = sortedValues.map((value) => ({
                    text: String(value),
                    value: value,
                }));

                column.onFilter = (value: any, record: any) => {
                    const recordValue = record[key];
                    return recordValue === value;
                };

                column.filterMultiple = true;
                column.filterSearch = true;
            }
        }

        if (!column.width) {
            if (typeof data[0]?.[key] === "number") {
                column.width = 120;
            } else if (typeof data[0]?.[key] === "boolean") {
                column.width = 80;
            } else {
                column.width = 150;
            }
        }

        return column;
    });

    return columns;
};

export const generateRawDataTableDataSource = (data: Record<string, any>[]): Record<string, any>[] => {
    return data.map((item, index) => ({
        ...item,
        key: item.id || item.key || `row-${index}`,
    }));
};

export const RawDataTable: React.FC<RawDataTableConfig> = ({
    data,
    fixField,
    priorityFields = [],
    excludeFields = [],
    fieldLabels = {},
    customRenderers = {},
    tableProps = {},
}) => {
    const columns = generateRawDataTableColumns(data, {
        fixField,
        priorityFields,
        excludeFields,
        fieldLabels,
        customRenderers,
    });

    const dataSource = generateRawDataTableDataSource(data);

    const defaultTableProps = {
        columns,
        dataSource,
        bordered: true,
        scroll: { x: "max-content", y: 800 },
        ...tableProps,
    };

    return <Table {...defaultTableProps} />;
};

const formatFieldName = (fieldName: string): string => {
    return fieldName
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase())
        .replace(/\s+/g, " ")
        .trim();
};

export type { ColumnsType };
